import React, { useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SEEN_KEY = "nah_last_alert_seen";
const POLL_MS = 20_000;

// Lightweight global polling component mounted once at the AppLayout level.
// Polls /api/alerts/recent every 20s and toasts whenever a new course is
// published. Stores the last-seen timestamp in localStorage so each user
// only ever sees each alert once across sessions.
export default function AlertWatcher() {
  const navigate = useNavigate();
  const lastSeenRef = useRef(localStorage.getItem(SEEN_KEY) || "");
  const initRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const params = lastSeenRef.current ? `?since=${encodeURIComponent(lastSeenRef.current)}` : "";
        const r = await axios.get(`${BACKEND_URL}/api/alerts/recent${params}`, {
          withCredentials: true,
        });
        if (cancelled) return;
        const alerts = r.data?.alerts || [];
        if (!alerts.length) return;
        // alerts are returned newest-first; show oldest first so the newest
        // is the toast that lingers on top.
        const ordered = [...alerts].reverse();
        // On the very first run we DON'T toast — we silently catch up the
        // last-seen pointer so users coming back to the dashboard aren't
        // flooded with old announcements.
        if (!initRef.current) {
          initRef.current = true;
          const newest = alerts[0].created_at;
          lastSeenRef.current = newest;
          localStorage.setItem(SEEN_KEY, newest);
          // If we never had a pointer before, surface only the SINGLE most
          // recent course so the user knows the feature exists.
          if (!localStorage.getItem(SEEN_KEY + "_init")) {
            localStorage.setItem(SEEN_KEY + "_init", "1");
            showAlert(alerts[0]);
          }
          return;
        }
        for (const a of ordered) {
          showAlert(a);
        }
        lastSeenRef.current = alerts[0].created_at;
        localStorage.setItem(SEEN_KEY, alerts[0].created_at);
      } catch (_e) {
        // Silent — not signed in or transient network blip.
      }
    };

    const showAlert = (a) => {
      const code = a?.meta?.share_code;
      toast.custom(
        (id) => (
          <div
            data-testid={`alert-toast-${a.alert_id}`}
            className="surface-solid border-2 border-[#FFCC00] bg-black p-4 max-w-sm flex gap-3"
            style={{ boxShadow: "0 0 22px rgba(255,204,0,0.35)" }}
          >
            <Sparkles className="w-5 h-5 text-[#FFCC00] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-heading text-base text-[#FFCC00] uppercase tracking-tight">
                {a.title}
              </div>
              <div className="text-xs text-white/80 mt-1">{a.message}</div>
              <div className="flex gap-2 mt-3">
                {code && (
                  <button
                    data-testid={`alert-toast-${a.alert_id}-run`}
                    onClick={() => {
                      toast.dismiss(id);
                      navigate(`/courses/play/${code}`);
                    }}
                    className="btn-primary text-[10px] tracking-widest uppercase px-3 py-1.5"
                  >
                    Run it
                  </button>
                )}
                <button
                  data-testid={`alert-toast-${a.alert_id}-dismiss`}
                  onClick={() => toast.dismiss(id)}
                  className="btn-ghost text-[10px] tracking-widest uppercase px-3 py-1.5"
                >
                  Later
                </button>
              </div>
            </div>
          </div>
        ),
        { duration: 12_000, id: a.alert_id },
      );
    };

    poll();
    const t = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [navigate]);

  return null;
}
